import React, { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { formatMoney, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileBarChart2 } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, Tooltip, CartesianGrid, XAxis, YAxis, ResponsiveContainer, Legend } from "recharts";

export default function Reports() {
  const [start, setStart] = useState(() => todayISO().slice(0, 8) + "01");
  const [end, setEnd] = useState(todayISO());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", { params: { start_date: start, end_date: end, limit: 5000 } });
      const perMonth = {};
      for (const t of data) {
        const m = t.date.slice(0, 7);
        if (!perMonth[m]) perMonth[m] = { month: m, income: 0, expense: 0 };
        if (t.type === "income") perMonth[m].income += t.amount_try;
        else perMonth[m].expense += t.amount_try;
      }
      const incomeTotal = data.filter(t => t.type === "income").reduce((s, t) => s + t.amount_try, 0);
      const expenseTotal = data.filter(t => t.type === "expense").reduce((s, t) => s + t.amount_try, 0);
      setStats({ chart: Object.values(perMonth).sort((a, b) => a.month.localeCompare(b.month)), incomeTotal, expenseTotal, count: data.length });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/reports/pdf`, { params: { start_date: start, end_date: end }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fintr_rapor_${start}_${end}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF indirildi");
    } catch (e) { toast.error("PDF oluşturulamadı"); }
  };

  return (
    <div className="space-y-6 slide-up" data-testid="reports-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Raporlar</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Finansal Tablolar</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Dönem bazlı gelir-gider analizleri ve PDF dışa aktarım.</p>
        </div>
        <Button data-testid="pdf-export-btn" onClick={downloadPdf} className="bg-indigo-500 hover:bg-indigo-600"><Download className="w-4 h-4 mr-2"/>PDF İndir</Button>
      </div>

      <div className="card-elev p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="text-xs text-[#94A3B8]">Başlangıç</Label>
            <Input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} className="bg-[#0F172A] border-[#1E293B] mt-1"/>
          </div>
          <div>
            <Label className="text-xs text-[#94A3B8]">Bitiş</Label>
            <Input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} className="bg-[#0F172A] border-[#1E293B] mt-1"/>
          </div>
          <div>
            <Button data-testid="report-apply" onClick={load} className="w-full bg-indigo-500 hover:bg-indigo-600" disabled={loading}>
              {loading ? "Yükleniyor…" : "Uygula"}
            </Button>
          </div>
        </div>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="card-elev p-5">
              <div className="text-xs text-[#94A3B8] uppercase">Toplam Gelir (TRY)</div>
              <div className="text-2xl font-bold mono mt-2 text-emerald-400">{formatMoney(stats.incomeTotal, "TRY")}</div>
            </div>
            <div className="card-elev p-5">
              <div className="text-xs text-[#94A3B8] uppercase">Toplam Gider (TRY)</div>
              <div className="text-2xl font-bold mono mt-2 text-red-400">{formatMoney(stats.expenseTotal, "TRY")}</div>
            </div>
            <div className="card-elev p-5">
              <div className="text-xs text-[#94A3B8] uppercase">Net Nakit (TRY)</div>
              <div className={`text-2xl font-bold mono mt-2 ${stats.incomeTotal - stats.expenseTotal >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatMoney(stats.incomeTotal - stats.expenseTotal, "TRY")}
              </div>
            </div>
          </div>

          <div className="card-elev p-6">
            <div className="font-semibold text-lg mb-4">Aylık Gelir / Gider</div>
            {stats.chart.length === 0 ? (
              <div className="text-sm text-[#94A3B8] py-12 text-center">
                <FileBarChart2 className="w-10 h-10 mx-auto mb-3 text-[#1E293B]"/>Bu tarih aralığında veri yok.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={stats.chart}>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="month" stroke="#64748B" fontSize={11}/>
                  <YAxis stroke="#64748B" fontSize={11}/>
                  <Tooltip contentStyle={{ background: "#131D31", border: "1px solid #1E293B", borderRadius: 12, color: "#F8FAFC" }} formatter={(v) => formatMoney(v, "TRY")}/>
                  <Legend wrapperStyle={{ color: "#94A3B8", fontSize: 12 }}/>
                  <Bar dataKey="income" name="Gelir" fill="#10B981" radius={[6, 6, 0, 0]}/>
                  <Bar dataKey="expense" name="Gider" fill="#EF4444" radius={[6, 6, 0, 0]}/>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </>
      )}
    </div>
  );
}
